import { nextTick } from 'vue'
import { iframeCommunication } from './iframeCommunication'

const PRIORITY_SCROLL_SELECTORS = [
  '.mobile-betslip-content',
  '.desktop-modal-box',
  '.desktop-modal-overlay',
  '.modal-body',
  '.modal-box',
  '.betslip-wrapper',
  '.app-content',
  '.main-content-wrapper',
]

function isScrollable(element: HTMLElement): boolean {
  const style = window.getComputedStyle(element)
  const allowsScroll = /(auto|scroll|overlay)/.test(style.overflowY)
  const isKnownContainer = PRIORITY_SCROLL_SELECTORS.some((selector) => element.matches(selector))
  return (allowsScroll || isKnownContainer) && element.scrollHeight > element.clientHeight + 1
}

export function collectScrollContainers(element: HTMLElement): HTMLElement[] {
  const containers = new Set<HTMLElement>()

  for (const selector of PRIORITY_SCROLL_SELECTORS) {
    const match = element.closest(selector)
    if (match instanceof HTMLElement) {
      containers.add(match)
    }
  }

  let parent = element.parentElement
  while (parent) {
    if (isScrollable(parent)) {
      containers.add(parent)
    }
    parent = parent.parentElement
  }

  if (iframeCommunication.isRunningInIframe()) {
    if (document.body.scrollHeight > document.body.clientHeight) {
      containers.add(document.body)
    }
    if (document.documentElement.scrollHeight > document.documentElement.clientHeight) {
      containers.add(document.documentElement)
    }
  }

  return Array.from(containers)
}

export function scrollElementIntoView(
  element: HTMLElement | null | undefined,
  options: {
    behavior?: ScrollBehavior
    block?: ScrollLogicalPosition
    focus?: boolean
    offset?: number
  } = {}
): void {
  if (!element) return

  const behavior = options.behavior ?? 'smooth'
  const block = options.block ?? 'start'
  const offset = options.offset ?? 16
  const containers = collectScrollContainers(element)

  for (const container of containers) {
    const containerRect = container.getBoundingClientRect()
    const targetRect = element.getBoundingClientRect()
    const nextTop = targetRect.top - containerRect.top + container.scrollTop - offset
    container.scrollTo({ top: Math.max(0, nextTop), behavior })
  }

  element.scrollIntoView({ behavior, block, inline: 'nearest' })

  if (options.focus !== false) {
    if (!element.hasAttribute('tabindex')) {
      element.setAttribute('tabindex', '-1')
    }
    element.focus({ preventScroll: true })
  }

  if (iframeCommunication.isRunningInIframe()) {
    iframeCommunication.sendImmediateHeightUpdate()
  } else {
    const rect = element.getBoundingClientRect()
    if (rect.top < offset || rect.bottom > window.innerHeight - offset) {
      window.scrollTo({
        top: window.scrollY + rect.top - offset,
        behavior,
      })
    }
  }
}

export async function scrollElementIntoViewAfterRender(
  getElement: () => HTMLElement | null | undefined,
  options?: Parameters<typeof scrollElementIntoView>[1]
): Promise<void> {
  await nextTick()
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      scrollElementIntoView(getElement(), options)
    })
  })
}
